/**
 * Maps ClauseFlag objects from the clause-review API into ReviewSuggestion objects
 * that the existing ReviewCard/ReviewPanel can render.
 *
 * Design decisions:
 * - critical severity → high priority + "Critical" tag (preserves distinction)
 * - reviewBasis drives category: missing_clause → addition, others → rewrite
 * - requiresHumanReview, low confidence, jurisdictionNote → visible tags
 * - escalated status → pending + "Escalated" tag (ReviewCard doesn't support escalated natively)
 */
import type { ClauseFlag, ClauseReviewCoverage } from '@corner/shared';
import type { ReviewSuggestion, SuggestionPriority, SuggestionCategory, SuggestionStatus } from '../types/review';

// ─── Severity → Priority ────────────────────────────────────────────

function mapSeverity(severity: ClauseFlag['severity']): { priority: SuggestionPriority; isCritical: boolean } {
  switch (severity) {
    case 'critical': return { priority: 'high', isCritical: true };
    case 'high':     return { priority: 'high', isCritical: false };
    case 'medium':   return { priority: 'medium', isCritical: false };
    case 'low':
    case 'info':
    default:         return { priority: 'low', isCritical: false };
  }
}

// ─── ReviewBasis → Category ─────────────────────────────────────────

function mapCategory(reviewBasis: ClauseFlag['reviewBasis']): SuggestionCategory {
  switch (reviewBasis) {
    case 'missing_clause': return 'addition';
    // Future: if we add explicit removal-oriented flags, map to 'deletion'
    default: return 'rewrite';
  }
}

// ─── Status Mapping ─────────────────────────────────────────────────

function mapStatus(status: ClauseFlag['status']): { mappedStatus: SuggestionStatus; isEscalated: boolean } {
  switch (status) {
    case 'accepted':  return { mappedStatus: 'accepted', isEscalated: false };
    case 'dismissed': return { mappedStatus: 'dismissed', isEscalated: false };
    case 'escalated': return { mappedStatus: 'pending', isEscalated: true };
    case 'pending':
    default:          return { mappedStatus: 'pending', isEscalated: false };
  }
}

// ─── Tag Builder ────────────────────────────────────────────────────

function buildTags(flag: ClauseFlag, isCritical: boolean, isEscalated: boolean): string[] {
  const tags: string[] = [];

  // Severity distinction
  if (isCritical) tags.push('Critical');

  // Review basis (human-readable)
  const basisLabels: Record<string, string> = {
    playbook_deviation: 'Playbook deviation',
    missing_clause: 'Missing clause',
    state_law_concern: 'State law concern',
    one_sided_risk: 'One-sided risk',
    ambiguity: 'Ambiguous language',
    business_risk: 'Business risk',
  };
  const basisLabel = basisLabels[flag.reviewBasis];
  if (basisLabel) tags.push(basisLabel);

  // Escalation
  if (isEscalated) tags.push('Escalated');

  // Human review / confidence
  if (flag.requiresHumanReview) tags.push('Needs attorney review');
  if (flag.confidence === 'low' && flag.confidenceReason) {
    tags.push(`Low confidence: ${flag.confidenceReason}`);
  } else if (flag.confidence === 'low') {
    tags.push('Low confidence');
  }

  // Jurisdiction note
  if (flag.jurisdictionNote) tags.push(flag.jurisdictionNote);

  // Original tags from Claude (filtered to avoid duplicates)
  for (const t of flag.tags) {
    if (!tags.includes(t)) tags.push(t);
  }

  if ((flag.evidenceQuote === '(evidence quote unavailable)' || !flag.suggestedFallback?.trim()) && !tags.includes('Needs manual review')) {
    tags.push('Needs manual review');
  }

  return tags;
}

// ─── Deviation Note Builder ─────────────────────────────────────────

/**
 * Build a directional deviation note based on reviewBasis and severity.
 * Exported for testing.
 */
export function buildDeviationNote(flag: ClauseFlag): string | null {
  const label = flag.clauseType.replace(/_/g, ' ');

  if (flag.reviewBasis === 'playbook_deviation') {
    if (flag.severity === 'critical' || flag.severity === 'high') {
      return `Less favorable than standard on ${label}`;
    }
    return `Differs from standard on ${label}`;
  }

  if (flag.reviewBasis === 'missing_clause') {
    if (flag.severity === 'critical' || flag.severity === 'high') {
      return `Missing standard protection: ${label}`;
    }
    return `Standard clause not found: ${label}`;
  }

  if (flag.reviewBasis === 'one_sided_risk') {
    return `One-sided terms on ${label}`;
  }

  return null;
}

// ─── Main Mapping Function ──────────────────────────────────────────

export function clauseFlagToReviewSuggestion(flag: ClauseFlag): ReviewSuggestion {
  const { priority, isCritical } = mapSeverity(flag.severity);
  const category = mapCategory(flag.reviewBasis);
  const { mappedStatus, isEscalated } = mapStatus(flag.status);
  const tags = buildTags(flag, isCritical, isEscalated);

  return {
    id: flag.flagId,
    sectionRef: flag.sectionRef,
    sectionTitle: flag.sectionTitle,
    priority,
    category,
    summary: flag.issue,
    originalText: flag.evidenceQuote,
    proposedText: flag.suggestedFallback,
    rationale: flag.rationale,
    tags,
    shortLabel: flag.clauseType.replace(/_/g, ' '),
    status: mappedStatus,
    deviationNote: buildDeviationNote(flag),
    appliedToDocument: false,
    applyFailed: null,
    incomplete: flag.evidenceQuote === '(evidence quote unavailable)' || !flag.suggestedFallback?.trim(),
  };
}

// ─── Coverage Description ───────────────────────────────────────────

/**
 * Returns a user-facing coverage description, or null if the document was fully reviewed.
 */
export function getCoverageNote(coverage: ClauseReviewCoverage): string | null {
  if (!coverage.truncated) return null;

  const pct = Math.round((coverage.reviewedCharacters / coverage.textLength) * 100);
  return `Reviewed approximately ${pct}% of the document. Later sections including exhibits and addenda may not be covered.`;
}
